// src/components/UserMenu.jsx
import { Link } from 'react-router-dom';
import { useAuth0 } from '@/hooks/useAuth';
import { useAccountStatus } from '@/hooks/useAccountStatus';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Avatar from '@radix-ui/react-avatar';
import { LogOut, ShieldCheck, Mail, Fingerprint, Calendar, Users, MessageCircle, Smartphone } from 'lucide-react';
import { FEATURES } from '@/lib/config';
import { format } from 'date-fns'; // Se você já tiver o date-fns no projeto
import { ptBR } from 'date-fns/locale';

export function UserMenu() {
  const { user, logout } = useAuth0();
  const { isAdmin } = useAccountStatus();

  if (!user) return null;

  // Formata a data de atualização se disponível
  const lastUpdate = user.updated_at 
    ? format(new Date(user.updated_at), "dd 'de' MMMM", { locale: ptBR })
    : null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="flex items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800">
          <Avatar.Root className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center overflow-hidden border-2 border-white dark:border-gray-800 shadow-sm">
            <Avatar.Image src={user.picture} className="h-full w-full object-cover" />
            <Avatar.Fallback className="text-sm font-bold text-blue-700 dark:text-blue-300">
              {user.name?.substring(0, 2).toUpperCase()}
            </Avatar.Fallback>
          </Avatar.Root>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content 
          align="start" 
          sideOffset={8}
          className="z-50 min-w-[260px] ml-4 overflow-hidden rounded-xl border bg-white shadow-2xl animate-in fade-in-80 slide-in-from-top-2 dark:border-gray-700 dark:bg-gray-900"
        >
          {/* Cabeçalho do Perfil */}
          <div className="bg-gray-50 dark:bg-gray-800/50 p-4 border-b dark:border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <Avatar.Root className="h-12 w-12 rounded-full overflow-hidden border-2 border-white dark:border-gray-700 shadow-sm">
                <Avatar.Image src={user.picture} className="object-cover" />
                <Avatar.Fallback>{user.name?.substring(0, 2).toUpperCase()}</Avatar.Fallback>
              </Avatar.Root>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                  {user.name}
                </p>
                <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 font-medium">
                  <ShieldCheck className="h-3 w-3" />
                  <span>Conta Verificada</span>
                </div>
              </div>
            </div>
          </div>

          {/* Detalhes do Usuário */}
          <div className="p-2 space-y-1">
            <div className="flex items-center gap-3 px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
              <Mail className="h-4 w-4 text-gray-400" />
              <span className="truncate">{user.email}</span>
            </div>
            
            {lastUpdate && (
              <div className="flex items-center gap-3 px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span>Atualizado em {lastUpdate}</span>
              </div>
            )}

            <div className="flex items-center gap-3 px-3 py-2 text-xs text-gray-500 dark:text-gray-500 italic border-t dark:border-gray-800 mt-2">
              <Fingerprint className="h-4 w-4" />
              <span className="truncate">ID: {user.sub?.split('|')[1]}</span>
            </div>
          </div>

          {/* Ações */}
          <div className="p-1 bg-gray-50 dark:bg-gray-800/30 border-t dark:border-gray-700">
            {isAdmin && (
              <DropdownMenu.Item asChild>
                <Link
                  to="/admin/contas"
                  className="flex w-full cursor-pointer items-center rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 outline-none hover:bg-gray-100 focus:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800 dark:focus:bg-gray-800"
                >
                  <Users className="mr-3 h-4 w-4" />
                  Gestão de Contas
                </Link>
              </DropdownMenu.Item>
            )}
            {isAdmin && (
              <DropdownMenu.Item asChild>
                <Link
                  to="/admin/whatsapp"
                  className="flex w-full cursor-pointer items-center rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 outline-none hover:bg-gray-100 focus:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800 dark:focus:bg-gray-800"
                >
                  <Smartphone className="mr-3 h-4 w-4" />
                  WhatsApp do condomínio
                </Link>
              </DropdownMenu.Item>
            )}
            {isAdmin && FEATURES.WHATSAPP_CONCILIACAO && (
              <DropdownMenu.Item asChild>
                <Link
                  to="/admin/conciliacao-whatsapp"
                  className="flex w-full cursor-pointer items-center rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 outline-none hover:bg-gray-100 focus:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800 dark:focus:bg-gray-800"
                >
                  <MessageCircle className="mr-3 h-4 w-4" />
                  Conciliação WhatsApp
                </Link>
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Item
              onClick={logout}
              className="flex w-full cursor-pointer items-center rounded-lg px-3 py-2.5 text-sm font-medium text-red-600 outline-none hover:bg-red-50 focus:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 dark:focus:bg-red-900/20"
            >
              <LogOut className="mr-3 h-4 w-4" />
              Sair do Sistema
            </DropdownMenu.Item>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
